import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  ReconciledItem,
  ReconciliationSummary,
  DailyCollectionTrend,
  RazorpayColumnMapping,
  ErpColumnMapping,
  TableFilterState,
  ReconciliationStatus,
} from '../types/reconciliation';
import { HistoricalRun } from '../types/auth';
import { runReconciliationEngine } from '../engine/reconciler';
import {
  generateEnterpriseSampleData,
  DEFAULT_RAZORPAY_MAPPING,
  DEFAULT_ERP_MAPPING,
} from '../engine/sampleData';
import { useAuth } from './AuthContext';

interface ParsedFileInfo {
  fileName: string;
  rowCount: number;
  headers: string[];
  rows: Record<string, any>[];
}

interface ReconciliationContextType {
  // Input State
  razorpayInfo: ParsedFileInfo | null;
  erpInfo: ParsedFileInfo | null;
  rpMapping: RazorpayColumnMapping;
  erpMapping: ErpColumnMapping;
  setRazorpayData: (info: ParsedFileInfo, autoDetectedMapping?: RazorpayColumnMapping) => void;
  setErpData: (info: ParsedFileInfo, autoDetectedMapping?: ErpColumnMapping) => void;
  updateRpMapping: (field: keyof RazorpayColumnMapping, column: string) => void;
  updateErpMapping: (field: keyof ErpColumnMapping, column: string) => void;

  // Execution State
  isProcessing: boolean;
  isReconciled: boolean;
  items: ReconciledItem[];
  filteredItems: ReconciledItem[];
  summary: ReconciliationSummary | null;
  trends: DailyCollectionTrend[];
  activeRunId: string | null;

  // Actions
  runReconciliation: () => Promise<void>;
  loadDemoData: () => Promise<void>;
  resetAll: () => void;

  // Filtering
  filterState: TableFilterState;
  updateFilters: (updates: Partial<TableFilterState>) => void;
  resetFilters: () => void;

  // Modals & Drawers
  selectedItemForAi: ReconciledItem | null;
  setSelectedItemForAi: (item: ReconciledItem | null) => void;
  selectedItemForBills: ReconciledItem | null;
  setSelectedItemForBills: (item: ReconciledItem | null) => void;

  // History & Archives
  historicalRuns: HistoricalRun[];
  reopenRun: (run: HistoricalRun) => void;
}

const DEFAULT_FILTER_STATE: TableFilterState = {
  searchQuery: '',
  paymentId: '',
  billNumber: '',
  customerName: '',
  statuses: [],
  refundStatus: 'all',
};

const ReconciliationContext = createContext<ReconciliationContextType | undefined>(undefined);

export const ReconciliationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logAction } = useAuth();

  const [razorpayInfo, setRazorpayInfo] = useState<ParsedFileInfo | null>(null);
  const [erpInfo, setErpInfo] = useState<ParsedFileInfo | null>(null);

  const [rpMapping, setRpMapping] = useState<RazorpayColumnMapping>(DEFAULT_RAZORPAY_MAPPING);
  const [erpMapping, setErpMapping] = useState<ErpColumnMapping>(DEFAULT_ERP_MAPPING);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isReconciled, setIsReconciled] = useState(false);
  const [items, setItems] = useState<ReconciledItem[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [trends, setTrends] = useState<DailyCollectionTrend[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const [filterState, setFilterState] = useState<TableFilterState>(DEFAULT_FILTER_STATE);

  const [selectedItemForAi, setSelectedItemForAi] = useState<ReconciledItem | null>(null);
  const [selectedItemForBills, setSelectedItemForBills] = useState<ReconciledItem | null>(null);

  const [historicalRuns, setHistoricalRuns] = useState<HistoricalRun[]>(() => {
    const saved = localStorage.getItem('schemepay_historical_runs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    return [];
  });

  // Save history to localStorage
  useEffect(() => {
    localStorage.setItem('schemepay_historical_runs', JSON.stringify(historicalRuns));
  }, [historicalRuns]);

  // Auto-run with pre-loaded enterprise dataset on initial load for immediate interactive demonstration!
  useEffect(() => {
    if (!isReconciled && !razorpayInfo && !erpInfo) {
      loadDemoData();
    }
  }, []);

  const setRazorpayData = (info: ParsedFileInfo, autoDetectedMapping?: RazorpayColumnMapping) => {
    setRazorpayInfo(info);
    if (autoDetectedMapping) {
      setRpMapping((prev) => ({ ...prev, ...autoDetectedMapping }));
    }
    setIsReconciled(false);
    logAction('FILE_UPLOAD', `Uploaded Razorpay report: ${info.fileName} (${info.rowCount} rows)`);
  };

  const setErpData = (info: ParsedFileInfo, autoDetectedMapping?: ErpColumnMapping) => {
    setErpInfo(info);
    if (autoDetectedMapping) {
      setErpMapping((prev) => ({ ...prev, ...autoDetectedMapping }));
    }
    setIsReconciled(false);
    logAction('FILE_UPLOAD', `Uploaded ERP report: ${info.fileName} (${info.rowCount} rows)`);
  };

  const updateRpMapping = (field: keyof RazorpayColumnMapping, column: string) => {
    setRpMapping((prev) => ({ ...prev, [field]: column }));
  };

  const updateErpMapping = (field: keyof ErpColumnMapping, column: string) => {
    setErpMapping((prev) => ({ ...prev, [field]: column }));
  };

  const runReconciliation = async () => {
    if (!razorpayInfo || !erpInfo) {
      alert('Please upload both Razorpay and ERP reports before running reconciliation.');
      return;
    }

    setIsProcessing(true);

    try {
      // Allow UI thread to breathe for progress state
      await new Promise((r) => setTimeout(r, 400));

      const result = runReconciliationEngine(
        razorpayInfo.rows,
        erpInfo.rows,
        rpMapping,
        erpMapping
      );

      const runId = 'RUN_' + Date.now().toString().slice(-6);
      setActiveRunId(runId);
      setItems(result.items);
      setSummary(result.summary);
      setTrends(result.trends);
      setIsReconciled(true);

      const newRun: HistoricalRun = {
        id: runId,
        runName: `Batch Reconciliation - ${new Date().toLocaleDateString('en-GB')}`,
        userId: user?.id || 'guest',
        userEmail: user?.email || 'finance@schemepay.com',
        razorpayFilename: razorpayInfo.fileName,
        erpFilename: erpInfo.fileName,
        summary: result.summary,
        createdAt: new Date().toISOString(),
      };

      setHistoricalRuns((prev) => [newRun, ...prev]);

      await logAction(
        'RECONCILIATION_RUN',
        `Reconciled ${result.summary.totalGroups} groups. Exact Matches: ${result.summary.exactMatches}, Match Rate: ${result.summary.matchPercentage}% in ${result.summary.processingTimeMs}ms.`
      );

      // Attempt background save to Cloudflare Workers / D1 API
      fetch('/api/reconcile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runName: newRun.runName,
          userId: newRun.userId,
          userEmail: newRun.userEmail,
          razorpayFilename: newRun.razorpayFilename,
          erpFilename: newRun.erpFilename,
          summary: result.summary,
          processingTimeMs: result.summary.processingTimeMs,
        }),
      }).catch(() => {});

    } catch (err: any) {
      console.error('Reconciliation error:', err);
      alert(`Reconciliation error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const loadDemoData = async () => {
    setIsProcessing(true);
    try {
      const sample = generateEnterpriseSampleData();

      const rpData: ParsedFileInfo = {
        fileName: 'Enterprise_Razorpay_Payments.xlsx',
        rowCount: sample.razorpayRows.length,
        headers: Object.keys(sample.razorpayRows[0]),
        rows: sample.razorpayRows,
      };

      const erpData: ParsedFileInfo = {
        fileName: 'Enterprise_ERP_MultiBill_Ledger.xlsx',
        rowCount: sample.erpRows.length,
        headers: Object.keys(sample.erpRows[0]),
        rows: sample.erpRows,
      };

      setRazorpayInfo(rpData);
      setErpInfo(erpData);
      setRpMapping(sample.razorpayMapping);
      setErpMapping(sample.erpMapping);

      const result = runReconciliationEngine(
        sample.razorpayRows,
        sample.erpRows,
        sample.razorpayMapping,
        sample.erpMapping
      );

      const runId = 'RUN_DEMO_' + Date.now().toString().slice(-4);
      setActiveRunId(runId);
      setItems(result.items);
      setSummary(result.summary);
      setTrends(result.trends);
      setIsReconciled(true);

      const demoRun: HistoricalRun = {
        id: runId,
        runName: 'Enterprise Golden Master Dataset',
        userId: user?.id || 'user_finance_01',
        userEmail: user?.email || 'finance@schemepay.com',
        razorpayFilename: rpData.fileName,
        erpFilename: erpData.fileName,
        summary: result.summary,
        createdAt: new Date().toISOString(),
      };

      setHistoricalRuns((prev) => {
        if (prev.some((r) => r.id === runId)) return prev;
        return [demoRun, ...prev];
      });

      await logAction('DEMO_LOADED', 'Loaded enterprise sample dataset with 1-to-many ERP bills & 6 status rules.');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetAll = () => {
    setRazorpayInfo(null);
    setErpInfo(null);
    setItems([]);
    setSummary(null);
    setTrends([]);
    setIsReconciled(false);
    setActiveRunId(null);
    setFilterState(DEFAULT_FILTER_STATE);
  };

  const updateFilters = (updates: Partial<TableFilterState>) => {
    setFilterState((prev) => ({ ...prev, ...updates }));
  };

  const resetFilters = () => {
    setFilterState(DEFAULT_FILTER_STATE);
  };

  const reopenRun = (run: HistoricalRun) => {
    // Re-run matching with existing data or sample data if matched
    loadDemoData();
    logAction('REOPEN_RUN', `Reopened historical reconciliation run: ${run.runName} (${run.id})`);
  };

  // Filtered items logic
  const filteredItems = items.filter((item) => {
    // Search query matches Payment ID, Customer Name, or Bill Numbers
    if (filterState.searchQuery) {
      const q = filterState.searchQuery.toLowerCase().trim();
      const matchPid = item.paymentId.toLowerCase().includes(q);
      const matchCust = item.customerName.toLowerCase().includes(q);
      const matchBills = item.billNumbers.some((b) => b.toLowerCase().includes(q));
      if (!matchPid && !matchCust && !matchBills) return false;
    }

    if (filterState.paymentId && !item.paymentId.toLowerCase().includes(filterState.paymentId.toLowerCase())) {
      return false;
    }

    if (filterState.customerName && !item.customerName.toLowerCase().includes(filterState.customerName.toLowerCase())) {
      return false;
    }

    if (filterState.billNumber && !item.billNumbers.some((b) => b.toLowerCase().includes(filterState.billNumber.toLowerCase()))) {
      return false;
    }

    if (filterState.statuses.length > 0 && !filterState.statuses.includes(item.status)) {
      return false;
    }

    if (filterState.minAmount !== undefined && item.razorpayAmount < filterState.minAmount && item.erpTotalAmount < filterState.minAmount) {
      return false;
    }

    if (filterState.maxAmount !== undefined && item.razorpayAmount > filterState.maxAmount && item.erpTotalAmount > filterState.maxAmount) {
      return false;
    }

    if (filterState.refundStatus === 'refunded' && item.refundStatus !== 'Refunded') {
      return false;
    }

    if (filterState.refundStatus === 'not_refunded' && item.refundStatus === 'Refunded') {
      return false;
    }

    if (filterState.startDate) {
      const itemDate = item.paymentDate.split(' ')[0];
      if (itemDate !== 'N/A' && itemDate < filterState.startDate) {
        return false;
      }
    }

    if (filterState.endDate) {
      const itemDate = item.paymentDate.split(' ')[0];
      if (itemDate !== 'N/A' && itemDate > filterState.endDate) {
        return false;
      }
    }

    return true;
  });

  return (
    <ReconciliationContext.Provider
      value={{
        razorpayInfo,
        erpInfo,
        rpMapping,
        erpMapping,
        setRazorpayData,
        setErpData,
        updateRpMapping,
        updateErpMapping,
        isProcessing,
        isReconciled,
        items,
        filteredItems,
        summary,
        trends,
        activeRunId,
        runReconciliation,
        loadDemoData,
        resetAll,
        filterState,
        updateFilters,
        resetFilters,
        selectedItemForAi,
        setSelectedItemForAi,
        selectedItemForBills,
        setSelectedItemForBills,
        historicalRuns,
        reopenRun,
      }}
    >
      {children}
    </ReconciliationContext.Provider>
  );
};

export const useReconciliation = () => {
  const context = useContext(ReconciliationContext);
  if (!context) {
    throw new Error('useReconciliation must be used within a ReconciliationProvider');
  }
  return context;
};
